import { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Maximize2, X, Shield, ShieldOff, Settings2,
} from 'lucide-react';

interface RemoteDesktopViewerProps {
  tunnelUrl: string;
  sessionToken: string;
  onDisconnect: () => void;
  onError: (error: string) => void;
}

type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

interface MonitorInfo {
  index: number;
  width: number;
  height: number;
  primary: boolean;
  name: string;
}

export default function RemoteDesktopViewer({ tunnelUrl, sessionToken, onDisconnect, onError }: RemoteDesktopViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fpsCountRef = useRef({ frames: 0, lastCalc: Date.now(), display: 0, total: 0 });
  const [totalFrames, setTotalFrames] = useState(0);

  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [monitors, setMonitors] = useState<MonitorInfo[]>([]);
  const [selectedMonitor, setSelectedMonitor] = useState(0);
  const [fps, setFps] = useState(15);
  const [quality, setQuality] = useState(50);
  const [scale, setScale] = useState(0.75);
  const [privacy, setPrivacy] = useState(false);
  const [displayFps, setDisplayFps] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const remoteSizeRef = useRef({ width: 1920, height: 1080 });

  // Track which keys are currently held down to prevent repeat events
  const heldKeysRef = useRef(new Set<string>());
  // Whether the canvas is "active" (user clicked it) for keyboard capture
  const canvasActiveRef = useRef(false);
  // Throttle mouse moves — 16ms = ~60Hz
  const lastMouseSendRef = useRef(0);
  // Frame-skip: only render the latest frame, discard stale ones
  const pendingFrameRef = useRef<ArrayBuffer | null>(null);
  const renderingRef = useRef(false);
  // Canvas size (updated via ResizeObserver, not per-frame)
  const canvasSizeRef = useRef({ w: 0, h: 0 });

  // Stable refs for callback props — prevents WebSocket from being torn down on every render
  const onErrorRef = useRef(onError);
  const onDisconnectRef = useRef(onDisconnect);
  onErrorRef.current = onError;
  onDisconnectRef.current = onDisconnect;

  // ── Send input to agent via WebSocket text frame ───────────────────────

  const send = useCallback((msg: object) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  // ── WebSocket connection (created once, reconnects with backoff) ───────

  useEffect(() => {
    const intentionalCloseRef = { current: false };
    // A socket that never opened means the tunnel hostname was unreachable, not
    // that a working connection dropped. The two need different advice.
    const everOpenedRef = { current: false };

    function connect() {
      // Build WS URL from tunnel URL (http→ws, https→wss)
      const wsBase = tunnelUrl.replace(/^http/, 'ws');
      const wsUrl = `${wsBase}/remote-ws?token=${sessionToken}`;
      console.log('[RemoteDesktop] WS connecting to', wsUrl);

      const ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[RemoteDesktop] WS open');
        everOpenedRef.current = true;
        setConnectionState('connected');
        reconnectRef.current = 0;
        // Request monitors list
        ws.send(JSON.stringify({ type: 'monitors' }));
      };

      // Binary frames: [0x01][2B width BE][2B height BE][jpeg bytes]
      // Text frames: JSON (monitors list)
      const renderLatestFrame = async () => {
        const buf = pendingFrameRef.current;
        if (!buf) { renderingRef.current = false; return; }
        pendingFrameRef.current = null;
        renderingRef.current = true;

        const view = new DataView(buf);
        if (buf.byteLength < 5 || view.getUint8(0) !== 0x01) {
          renderingRef.current = false;
          return;
        }

        const width = view.getUint16(1);
        const height = view.getUint16(3);
        const jpegBytes = new Uint8Array(buf, 5);

        remoteSizeRef.current = { width, height };

        try {
          const blob = new Blob([jpegBytes], { type: 'image/jpeg' });
          const bitmap = await createImageBitmap(blob);

          const canvas = canvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext('2d');
            const { w, h } = canvasSizeRef.current;
            if (ctx && w > 0 && h > 0) {
              const scale = Math.min(w / width, h / height);
              const dx = (w - width * scale) / 2;
              const dy = (h - height * scale) / 2;
              ctx.fillStyle = '#000';
              ctx.fillRect(0, 0, w, h);
              ctx.drawImage(bitmap, dx, dy, width * scale, height * scale);
            }
          }
          bitmap.close();

          fpsCountRef.current.frames++;
          fpsCountRef.current.total++;
          const now = Date.now();
          if (now - fpsCountRef.current.lastCalc >= 1000) {
            fpsCountRef.current.display = fpsCountRef.current.frames;
            fpsCountRef.current.frames = 0;
            fpsCountRef.current.lastCalc = now;
            setDisplayFps(fpsCountRef.current.display);
            setTotalFrames(fpsCountRef.current.total);
          }
        } catch { /* decode failed, skip frame */ }

        // If another frame arrived while we were rendering, render it next
        renderLatestFrame();
      };

      ws.onmessage = (ev) => {
        if (ev.data instanceof ArrayBuffer) {
          // Binary frame — queue for render
          pendingFrameRef.current = ev.data;
          if (!renderingRef.current) renderLatestFrame();
        } else {
          // Text frame — JSON
          try {
            const data = JSON.parse(ev.data as string);
            if (Array.isArray(data)) {
              setMonitors(data);
            }
          } catch { /* ignore */ }
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (intentionalCloseRef.current) {
          setConnectionState('disconnected');
          return;
        }
        reconnectRef.current++;
        console.log(`[RemoteDesktop] WS closed, reconnect attempt ${reconnectRef.current}`);
        if (reconnectRef.current > 5) {
          setConnectionState('disconnected');
          onErrorRef.current(everOpenedRef.current
            ? 'Connection lost — station may have restarted'
            : 'Could not reach the station tunnel — close this and start Remote Desktop again to get a fresh link');
          onDisconnectRef.current();
        } else {
          setConnectionState('reconnecting');
          const delay = Math.min(1000 * Math.pow(2, reconnectRef.current - 1), 8000);
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        // onclose fires after onerror, handles reconnection
      };
    }

    connect();

    return () => {
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
    };
   
  }, [tunnelUrl, sessionToken]);

  // Fullscreen change listener
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Resize canvas via ResizeObserver (not per-frame)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width);
      const h = Math.round(entry.contentRect.height);
      if (w > 0 && h > 0) {
        canvas.width = w;
        canvas.height = h;
        canvasSizeRef.current = { w, h };
      }
    });
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [connectionState]);

  // ── Input handlers ─────────────────────────────────────────────────────

  /** Convert mouse event to normalized 0-1 coordinates relative to the rendered frame. */
  const normalizeMousePos = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const { width, height } = remoteSizeRef.current;
    const scale = Math.min(rect.width / width, rect.height / height);
    const dx = (rect.width - width * scale) / 2;
    const dy = (rect.height - height * scale) / 2;
    const x = (e.clientX - rect.left - dx) / (width * scale);
    const y = (e.clientY - rect.top - dy) / (height * scale);
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return { x, y };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const now = Date.now();
    if (now - lastMouseSendRef.current < 16) return; // ~60Hz throttle
    lastMouseSendRef.current = now;
    const pos = normalizeMousePos(e);
    if (pos) send({ type: 'mouse', ...pos, action: 'move', delta: 0 });
  }, [normalizeMousePos, send]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    canvasActiveRef.current = true;
    const pos = normalizeMousePos(e);
    if (!pos) return;
    const action = e.button === 2 ? 'rightclick' : 'mousedown';
    send({ type: 'mouse', ...pos, action, delta: 0 });
  }, [normalizeMousePos, send]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = normalizeMousePos(e);
    if (pos) send({ type: 'mouse', ...pos, action: 'mouseup', delta: 0 });
  }, [normalizeMousePos, send]);

  const handleDblClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = normalizeMousePos(e);
    if (pos) send({ type: 'mouse', ...pos, action: 'dblclick', delta: 0 });
  }, [normalizeMousePos, send]);

  const handleWheel = useCallback((e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const pos = normalizeMousePos(e);
    if (pos) send({ type: 'mouse', ...pos, action: 'scroll', delta: -Math.sign(e.deltaY) * 120 });
  }, [normalizeMousePos, send]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  // ── Document-level keyboard capture ─────────────────────────────────
  // Canvas keyboard events are unreliable across browsers. Instead, listen
  // at the document level when the canvas is "active" (clicked).

  const sendRef = useRef(send);
  sendRef.current = send;

  useEffect(() => {
    const KEY_MAP: Record<string, string> = {
      Backspace: 'Backspace', Tab: 'Tab', Enter: 'Enter', Escape: 'Escape',
      ' ': 'Space', ArrowLeft: 'ArrowLeft', ArrowUp: 'ArrowUp', ArrowRight: 'ArrowRight', ArrowDown: 'ArrowDown',
      Delete: 'Delete', Insert: 'Insert', Home: 'Home', End: 'End', PageUp: 'PageUp', PageDown: 'PageDown',
      F1: 'F1', F2: 'F2', F3: 'F3', F4: 'F4', F5: 'F5', F6: 'F6',
      F7: 'F7', F8: 'F8', F9: 'F9', F10: 'F10', F11: 'F11', F12: 'F12',
      Shift: 'Shift', Control: 'Control', Alt: 'Alt', Meta: 'Meta',
      CapsLock: 'CapsLock', NumLock: 'NumLock', ScrollLock: 'ScrollLock',
      PrintScreen: 'PrintScreen', Pause: 'Pause',
    };

    const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta']);

    const onKeyDown = (e: KeyboardEvent) => {
      if (!canvasActiveRef.current) return;
      e.preventDefault();
      e.stopPropagation();

      const specialKey = KEY_MAP[e.key];
      const hasModifiers = e.ctrlKey || e.altKey || e.metaKey;

      // Modifier keys: track held state, send down/up individually
      if (specialKey && MODIFIER_KEYS.has(specialKey)) {
        if (heldKeysRef.current.has(specialKey)) return;
        heldKeysRef.current.add(specialKey);
        sendRef.current({ type: 'key', key: specialKey, action: 'down', modifiers: [] });
        return;
      }

      // Special keys (arrows, F-keys, etc.) or modified keys (Ctrl+C): send as key press
      if (specialKey || hasModifiers) {
        const key = specialKey || (e.key.length === 1 ? e.key.toLowerCase() : null);
        if (!key) return;
        if (heldKeysRef.current.has(key)) return;
        heldKeysRef.current.add(key);
        const modifiers: string[] = [];
        if (e.ctrlKey) modifiers.push('ctrl');
        if (e.shiftKey) modifiers.push('shift');
        if (e.altKey) modifiers.push('alt');
        sendRef.current({ type: 'key', key, action: 'press', modifiers });
        return;
      }

      // Printable characters: use TEXT command (atomic, handles shift/layout natively)
      if (e.key.length === 1) {
        sendRef.current({ type: 'text', text: e.key });
        return;
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (!canvasActiveRef.current) return;
      e.preventDefault();
      const specialKey = KEY_MAP[e.key];
      const key = specialKey || (e.key.length === 1 ? e.key.toLowerCase() : null);
      if (!key) return;
      heldKeysRef.current.delete(key);
      // Only send key-up for modifier keys (they need explicit release)
      if (specialKey && MODIFIER_KEYS.has(specialKey)) {
        sendRef.current({ type: 'key', key: specialKey, action: 'up', modifiers: [] });
      }
    };

    // Deactivate canvas when clicking outside it
    const onMouseDown = (e: MouseEvent) => {
      if (canvasRef.current && !canvasRef.current.contains(e.target as Node)) {
        canvasActiveRef.current = false;
        heldKeysRef.current.clear();
      }
    };

    // Release all held keys when window loses focus
    const onBlur = () => {
      heldKeysRef.current.clear();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keyup', onKeyUp, true);
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('blur', onBlur);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('keyup', onKeyUp, true);
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // ── Config actions ─────────────────────────────────────────────────────

  const updateConfig = useCallback((cfg: { fps?: number; quality?: number; scale?: number }) => {
    if (cfg.fps) setFps(cfg.fps);
    if (cfg.quality) setQuality(cfg.quality);
    if (cfg.scale) setScale(cfg.scale);
    send({ type: 'config', ...cfg });
  }, [send]);

  const togglePrivacy = useCallback(() => {
    const next = !privacy;
    setPrivacy(next);
    send({ type: 'privacy', enabled: next });
  }, [privacy, send]);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen();
    }
  }, []);

  const switchMonitor = useCallback((index: number) => {
    setSelectedMonitor(index);
    send({ type: 'select_monitor', index });
  }, [send]);

  const handleDisconnect = useCallback(() => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectRef.current = 999;
    wsRef.current?.close();
    onDisconnectRef.current();
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className="flex flex-col bg-black rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-900 text-white text-xs">
        <div className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${
            connectionState === 'connected' ? 'bg-green-500' :
            connectionState === 'reconnecting' ? 'bg-yellow-500 animate-pulse' :
            connectionState === 'connecting' ? 'bg-blue-500 animate-pulse' :
            'bg-red-500'
          }`} />
          <span className="text-gray-400">
            {connectionState === 'connected' ? `${displayFps} fps | ${totalFrames} frames` :
             connectionState === 'reconnecting' ? 'Reconnecting...' :
             connectionState === 'connecting' ? 'Connecting...' : 'Disconnected'}
          </span>
        </div>

        <div className="flex-1" />

        {/* Monitor selector */}
        {monitors.length > 1 && (
          <select
            value={selectedMonitor}
            onChange={(e) => switchMonitor(Number(e.target.value))}
            className="bg-gray-800 text-white text-xs rounded px-1.5 py-0.5 border border-gray-700"
          >
            {monitors.map((m) => (
              <option key={m.index} value={m.index}>
                {m.primary ? 'Primary' : `Display ${m.index + 1}`} ({m.width}x{m.height})
              </option>
            ))}
          </select>
        )}

        {/* Settings */}
        <div className="relative">
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-800"
            onClick={() => setShowSettings(!showSettings)}>
            <Settings2 className="h-3.5 w-3.5" />
          </Button>
          {showSettings && (
            <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg p-3 space-y-3 z-50 min-w-[180px]">
              <div>
                <label className="text-[11px] text-gray-400 block mb-1">Target FPS: {fps}</label>
                <div className="flex gap-1">
                  {[5, 10, 15, 30, 60].map((f) => (
                    <button key={f}
                      className={`px-2 py-0.5 text-[11px] rounded ${fps === f ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                      onClick={() => updateConfig({ fps: f })}>{f}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] text-gray-400 block mb-1">Quality: {quality}%</label>
                <input type="range" min={20} max={90} value={quality}
                  onChange={(e) => updateConfig({ quality: Number(e.target.value) })}
                  className="w-full h-1 accent-blue-500" />
              </div>
              <div>
                <label className="text-[11px] text-gray-400 block mb-1">Resolution: {Math.round(scale * 100)}%</label>
                <div className="flex gap-1">
                  {[0.5, 0.75, 1].map((s) => (
                    <button key={s}
                      className={`px-2 py-0.5 text-[11px] rounded ${scale === s ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                      onClick={() => updateConfig({ scale: s })}>{Math.round(s * 100)}%</button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Privacy toggle */}
        <Button size="sm" variant="ghost"
          className={`h-6 w-6 p-0 ${privacy ? 'text-orange-400 hover:text-orange-300' : 'text-gray-400 hover:text-white'} hover:bg-gray-800`}
          onClick={togglePrivacy}
          title={privacy ? 'Privacy mode ON — station screen is black' : 'Privacy mode OFF'}>
          {privacy ? <ShieldOff className="h-3.5 w-3.5" /> : <Shield className="h-3.5 w-3.5" />}
        </Button>

        {/* Fullscreen */}
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-800"
          onClick={toggleFullscreen} title="Toggle fullscreen">
          <Maximize2 className="h-3.5 w-3.5" />
        </Button>

        {/* Disconnect */}
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-gray-800"
          onClick={handleDisconnect} title="Disconnect">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Canvas */}
      <div className="relative bg-black flex items-center justify-center"
        style={isFullscreen ? { height: '100vh' } : { height: '500px' }}>
        {connectionState === 'connecting' || connectionState === 'reconnecting' ? (
          <div className="text-gray-400 text-sm flex items-center gap-2">
            <span className="h-3 w-3 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
            {connectionState === 'connecting' ? 'Connecting to station...' : 'Reconnecting...'}
          </div>
        ) : connectionState === 'disconnected' ? (
          <div className="text-gray-400 text-sm">Disconnected</div>
        ) : (
          <canvas
            ref={canvasRef}
            className="w-full h-full cursor-crosshair outline-none"
            tabIndex={0}
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onDoubleClick={handleDblClick}
            onWheel={handleWheel}
            onContextMenu={handleContextMenu}
          />
        )}
      </div>

      {/* Footer hint */}
      {connectionState === 'connected' && (
        <div className="px-3 py-1 bg-gray-900 text-[10px] text-gray-500 text-center">
          Click canvas to capture keyboard input
          {privacy && <span className="ml-2 text-orange-400">Privacy mode active — station screen is black</span>}
        </div>
      )}
    </div>
  );
}
